/** Sub-tab bar splitting the Market page's five big sections (operator
 * request 2026-07-25: "too much data on one screen"). Heatmap is the
 * default; the last choice is remembered so a trading day can live on
 * one tab. Storage access is defensive — private-mode browsers (and the
 * test DOM) may not expose localStorage. */

import type { ReactElement } from "react";
import { TabBar as UiTabBar } from "@/components/ui/TabBar";

export type MarketTab = "heatmap" | "options" | "straddle" | "participants";

const TAB_STORAGE_KEY = "nss-market-tab";

const TABS: readonly { id: MarketTab; label: string; icon: string }[] = [
  { id: "heatmap", label: "Heatmap", icon: "ti-layout-grid" },
  { id: "options", label: "Options & Trends", icon: "ti-stairs" },
  { id: "straddle", label: "Straddle", icon: "ti-chart-line" },
  { id: "participants", label: "Participants", icon: "ti-users" },
];

export function readStoredTab(): MarketTab {
  try {
    const raw = window.localStorage.getItem(TAB_STORAGE_KEY);
    // "trends" predates the 2026-07-27 merge into the Options tab.
    if (raw === "trends") return "options";
    return TABS.some((t) => t.id === raw) ? (raw as MarketTab) : "heatmap";
  } catch {
    return "heatmap";
  }
}

export function storeTab(tab: MarketTab): void {
  try {
    window.localStorage.setItem(TAB_STORAGE_KEY, tab);
  } catch {
    // Private mode / no storage — the choice just isn't remembered.
  }
}

export function TabBar({
  value,
  onChange,
}: Readonly<{ value: MarketTab; onChange: (tab: MarketTab) => void }>): ReactElement {
  return <UiTabBar tabs={TABS} value={value} onChange={onChange} />;
}
